import React, { useEffect, useState, useRef } from "react";
const _ = require("lodash");
import {
  Box,
  Flex,
  Text,
  VStack,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  useTheme,
  useColorMode,
  Button,
  useDisclosure,
  Spinner,
  Link,
  useToast,
  useColorModeValue,
  Portal
} from "@chakra-ui/react";

import {
  smsReminderTemplate,
  smsReviewTemplate,
  dataTemplate,
  getCampaignPlaceholders,
  getCampaignPreviewHtml
} from "./CampaignConstants";
import debounce from "lodash.debounce";
import dialogManager from "../DialogManager/DialogManager";
import { AddIcon } from "@chakra-ui/icons";
import { cqAPI, HOST, uploadString } from "../../lib/Auth";
import "./Campaigns.css";
import BeefreeSDK from "@beefree.io/sdk";
import "react-datepicker/dist/react-datepicker.css";
import PlaceholdersModal from "./PlaceholdersModal";
import CampaignSettings from "./CampaignSettings";
import CampaignTypes from "./CampaignTypes";
import AddCampaignModal from "./AddCampaignModal";
import SendTestModal from "./SendTestModal";
import PreviewModal from "./PreviewModal";
import TemplatesModal from "./TemplatesModal";
import SMSCampaignModal from "./SMSCampaignModal";
import PhoneCampaignModal from "./PhoneCampaignModal";
import CampaignTableRow from "./CampaignTableRow";
import TemplateEditorModal from "./TemplateEditorModal";

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [tabIndex, setTabIndex] = useState(1);
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const [categories, setCategories] = useState([]);
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterOptionEnabled, setFilterOptionEnabled] = useState({});
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);

  const [customizedTemplate, setCustomizedTemplate] = useState(dataTemplate);
  const [isBeePluginVisible, setIsBeePluginVisible] = useState(false);
  const [isConfigVisible, setIsConfigVisible] = useState(false);
  const [estimatedPatients, setEstimatedPatients] = useState("-");
  const [loading, setLoading] = useState(false);
  const [isSendTestModalOpen, setSendTestModalOpen] = useState(false);
  const [testDate, setTestDate] = useState(null);

  const textAreaRef = useRef(null);
  const placeholderModalRef = useRef(null);
  const activeElementRef = useRef(null);
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [templatePath, setTemplatePath] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [campaignType, setCampaignType] = useState("");
  const [campaignPlaceholders, setCampaignPlaceholders] = useState([]);
  const [campaignMedium, setCampaignMedium] = useState("");
  const [campaignCategory, setCampaignCategory] = useState("");
  const [beeEditorInstance, setBeeEditorInstance] = useState(null);
  const [isBeeEditorSaving, setBeeEditorSaving] = useState(false);
  const [timezone, setTimezone] = useState('America/Los_Angeles');

  const {
    isOpen: isAddCampaignOpen,
    onOpen: onAddCampaignOpen,
    onClose: onAddCampaignClose,
  } = useDisclosure();

  const {
    isOpen: isSMSCampaignOpen,
    onOpen: onSMSCampaignOpen,
    onClose: onSMSCampaignClose,
  } = useDisclosure();

  const {
    isOpen: isPhoneCampaignOpen,
    onOpen: onPhoneCampaignOpen,
    onClose: onPhoneCampaignClose,
  } = useDisclosure();

  const {
    isOpen: isTemplatesModalOpen,
    onOpen: onTemplatesModalOpen,
    onClose: onTemplatesModalClose,
  } = useDisclosure();

  const removeEmptyStrings = (obj) => {
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === "") {
          delete obj[key];
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          removeEmptyStrings(obj[key]);
          if (!(obj[key] instanceof Date) && Object.keys(obj[key]).length === 0) {
            delete obj[key];
          }
        }
      });
    }
  };

  const resetData = () => {
    setCustomizedTemplate(dataTemplate);
    setCampaignType("");
    setCampaignMedium("");
    setCampaignCategory("");
    setCampaignPlaceholders([]);
    setSelectedTemplate(null);
    setFilterOptionEnabled({});
    setTestDate(null);
    setIsEditing(false);
  };

  const fetchCampaigns = async () => {
    try {
      const response = await cqAPI.get(`/campaigns`);
      setCampaigns(response.data.campaigns.map(data=>({
        ...data,
        settings : {
          ...data.settings,
          filter : {
            ...data.settings.filter,
            patient_status : data.settings.filter.patient_status || 'all'
          },
          sending_date: {
            ...data.settings.sending_date,
            hours_since_the_event: data.settings?.sending_date?.hours_since_the_event && data.settings?.sending_date?.hours_since_the_event.toString()
          }
        }
      })));
      dataTemplate.template = response.data.model.template;
      setTimezone(response.data.timezone);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await cqAPI.get(`/campaigns/email-templates-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const saveTemplate = async (data, fileType, extension) => {
    const result = {url: null, type: null, msg: null};
    setUploadingTemplate(true);
    try {
      const response = await uploadString(
        data,
        fileType,
        String(Math.random() * 10000).split(".")[0] + `.${extension}`,
        false
      );
      result.url = HOST + response.url;
      result.type = response.type;
      result.msg = response.msg;
    } catch(err) {
      console.log('Error saving template', err)
      result.type = 'error'
      result.msg = err.msg || err.message;
    }
    setUploadingTemplate(false);
    return result;
  };

  const getTemplate = async () => {
    let newValue = { ...customizedTemplate };

    if (campaignMedium === "Email" && !customizedTemplate.template?.email?.html_template_url) {
      // No BEE editor URLs — use template_data so the API builds the template server-side
      const emailBody = customizedTemplate?.html ?? selectedTemplate?.html ?? "";
      newValue.body = emailBody;
      newValue.template_data = {
        body: emailBody,
        subject: customizedTemplate?.subject ?? "",
      };
    }

    if (campaignMedium === "SMS") {
      newValue.template_data = {
        template: customizedTemplate?.template?.sms?.template ?? "",
      };
    }

    if (campaignMedium === "Phone") {
      const phone = customizedTemplate?.template?.phone ?? {};
      newValue.template_data = {
        introduction_message: phone.introduction_message?.text ?? "",
        appointment_prototype_message: phone.appointment_prototype_message?.text ?? "",
        human_pickup_message: phone.human_pickup_message?.text ?? "",
        voice_mail_pickup_message: phone.voice_mail_pickup_message?.text ?? "",
        on_opt_out_response: phone.on_opt_out_response?.text ?? "",
        on_confirmation_response: phone.on_confirmation_response?.text ?? "",
      };
    }

    const clone = _.cloneDeep(newValue);
    removeEmptyStrings(clone);
    return clone;
  };

  const Camelize = (st) => st.replace(/(^.)/, (m) => m.toUpperCase());

  const getValue = (path, obj) => {
    const value = _.get(obj, path);
    return value !== undefined ? value : _.get(dataTemplate, path);
  };

  const showEmailTemplates = async (categoryId) => {
    try {
      const response = await cqAPI.get(
        `/campaigns/email-templates?id=${categoryId}`
      );
      setTemplates(response.data);
      onTemplatesModalOpen();
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  async function getCampaignHtml(campaign) {
    if (campaign?.emailTemplate && campaign?.emailTemplate[0]) {
      return campaign.emailTemplate[0].email_content;
    }

    try {
      const response = await fetch(campaign.template.email.html_template_url);
      if (response.ok) {
        return await response.text();
      }
      console.log('Template fetching failed with status', response.status);
    } catch (err) {
      console.log('Template fetching failed with error', err);
    }

    return "";
  }

  async function getCampaignContent(campaign) {
    try {
      const response = await fetch(campaign.template.email.json_template_url);
      if (response.ok) {
        const text = await response.text();
        return await JSON.parse(text);
      }
      console.log('Template JSON fetching failed with status', response.status);
    } catch (err) {
      console.log('Template JSON fetching failed with error', err);
    }

    return null;
  }

  const loadCampaignData = async (campaign) => {
    // Initialize nested properties
    campaign.settings = _.defaultsDeep(campaign.settings, dataTemplate.settings);

    if (campaign?.template?.email?.html_template_url) {
      campaign.html = await getCampaignHtml(campaign);
    } else {
      campaign.html = campaign?.emailTemplate?.email_content || '';
    }
    if (campaign?.template?.email?.json_template_url) {
      campaign.content = await getCampaignContent(campaign);
    }
    if (!campaign.content) {
      campaign.content = campaign?.emailTemplate?.json_content || {};
    }

    setCampaignType(campaign.purpose.replace("campaign-", ""));
    setCampaignMedium(
      campaign.type === "sms" ? "SMS" : campaign.type === "email" ? "Email" : "Phone"
    );
    setCampaignPlaceholders(getCampaignPlaceholders(campaign.purpose.replace("campaign-", "")));
    setCampaignCategory(campaign?.emailTemplateCategory?._id ?? "");
    setSelectedTemplate(campaign?.emailTemplate);
    setCustomizedTemplate(campaign);
    setFilterOptionEnabled({
      'settings.filter.providers': !!campaign?.settings?.filter?.providers?.list?.length,
      'settings.filter.appointment_type': !!campaign?.settings?.filter?.appointment_type?.list?.length,
      'settings.filter.treatment_codes': !!campaign?.settings?.filter?.treatment_codes?.list?.length,
      'settings.filter.patient_ids': !!campaign?.settings?.filter?.patient_ids?.list?.length,
      'settings.filter.outstanding_treatment_categories': !!campaign?.settings?.filter?.outstanding_treatment_categories?.length,
      'settings.filter.zip_codes': !!campaign?.settings?.filter?.zip_codes?.length
    });

    return campaign;
  }

  const loadSettingsEditor = async (campaign) => {
    await loadCampaignData(campaign);
    setIsConfigVisible(true);
  };

  const isRequired = (filter) => {
    if (filter.required === true) return true;
    if (typeof filter.required === "function") {
      return !!filter.required(campaignMedium);
    }
    return false;
  };


  const saveCampaign = async () => {
    setSavingCampaign(true);
    const campaignTypeConfig = CampaignTypes.find((c) => c.id === campaignType);
    const requiredFields = (campaignTypeConfig?.filters || []).filter(
      (filter) =>
        isRequired(filter) &&
        (!filter.condition ||
          filter.condition({
            medium: campaignMedium,
            template: customizedTemplate,
          }))
    );

    for (const field of requiredFields) {
      const value = getValue(field.id, customizedTemplate);
      if ((!value && value !== 0) || (Array.isArray(value) && value.length === 0)) {
        toast({
          title: "Missing Required Field",
          description: `${field.label} is required.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setSavingCampaign(false);
        return; // Exit function to stay in the edit window
      }
    }

    const data = await getTemplate();
    if (!data) {
      setSavingCampaign(false);
      return; // Exit function to stay in the edit window
    }

    data.settings = data.settings || {};
    data.settings.filter = data.settings.filter || {};

    if (data?.settings?.sending_date?.hours_since_the_event !== null) {
      data.settings.sending_date.hours_since_the_event = parseInt(data.settings.sending_date.hours_since_the_event)
    }
    if (data.html) {
      data.html = undefined;
    }

    setUploadingTemplate(true);
    try {
      const response = await cqAPI[data._id ? "put" : "post"](
        "/campaigns" + (data._id ? "?id=" + data._id : ""),
        {
          ...data,
          type: campaignMedium.toLowerCase(),
          purpose: campaignType === "reminder" ? "reminder" : `campaign-${campaignType}`,
          ...(selectedTemplate ? { template_id: selectedTemplate.id } : {}),
          settings : {
            ...data.settings,
            filter : {
              ...data.settings.filter,
              patient_status : data.settings.filter.patient_status === 'all'? null : data.settings.filter.patient_status
            }
          }
        }
      );

      if (response.type === "error") {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Campaign Saved",
          description: "Your campaign has been saved successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        resetData();
        await fetchCampaigns();
        setIsConfigVisible(false);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while saving the campaign. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingTemplate(false);
      setSavingCampaign(false);
    }
  };

  const saveEmailTemplate = async (jsonFile, htmlFile) => {
    const emailResult = await saveTemplate(
      htmlFile,
      'text/html',
      'html'
    );

    if (emailResult.type === "error") {
      toast({
        title: "Error",
        description: emailResult.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    const jsonResult = await saveTemplate(
      jsonFile,
      'application/json',
      'json'
    );

    if (jsonResult.type === "error") {
      toast({
        title: "Error",
        description: jsonResult.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    setCustomizedTemplate((prev) => ({
      ...prev,
      content: JSON.parse(jsonFile),
      html: htmlFile,
      template: {
        ...(prev?.template || {}),
        email: {
          ...(prev?.template?.email || {}),
          html_template_url: emailResult.url,
          json_template_url: jsonResult.url
        },
      },
    }));

    return true;
  };

  const handleBeeFreeSave = async (jsonFile, htmlFile) => {
    const result = await saveEmailTemplate(jsonFile, htmlFile)

    setIsBeePluginVisible(false);
    setIsTextAreaFocused(false);
    setBeeEditorInstance(null);

    if (!isEditing) {
      setIsConfigVisible(true);
    } else {
      setBeeEditorSaving(true);
    }
  };

  const showTemplateEditor = async (customizedTemplate) => {
    const resp = await cqAPI.get("/campaigns/email-template-edit-token");
    const beeConfig = {
      uid: "0",
      container: "bee-plugin-container",
      language: "en-US",
      onLoad: () => {
        setIsTextAreaFocused(true);
      },
      onSave: handleBeeFreeSave,
    };
    setIsBeePluginVisible(true);
    const builder = new BeefreeSDK(resp.data);
    setBeeEditorInstance(builder);
    await builder.start(
      beeConfig,
      customizedTemplate?.content || selectedTemplate.content
    );
  };

  useEffect(() => {
    const handler = async () => {
      if (isBeePluginVisible) {
        await showTemplateEditor(customizedTemplate);
      } else if (!isBeePluginVisible && isBeeEditorSaving) {
        setIsEditing(false);
        setBeeEditorSaving(false);
        await saveCampaign();
      }
    };

    handler();
  }, [isBeePluginVisible, isBeeEditorSaving]);

  const loadTemplateEditor = async (campaign) => {
    setIsEditing(true);

    // Initialize nested properties if any are missing
    const updatedCampaign = await loadCampaignData(campaign);

    switch (campaign.type) {
      case 'email':
        if (updatedCampaign?.content) {
          setCustomizedTemplate(updatedCampaign);
          setIsBeePluginVisible(true);
        } else {
          await showEmailTemplates(updatedCampaign?.template_id?.toString());
          toast({
            title: "Template not found",
            description: "Could not find the template",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        break;
      case 'phone':
        onPhoneCampaignOpen();
        break;
      case 'sms':
        onSMSCampaignOpen();
        break;
    }
  };

  const handleFocus = (path) => {
    setIsTextAreaFocused(true);
    textAreaRef.current = document.activeElement; // Set the ref to the currently focused element
    setTemplatePath(path);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (
        activeElementRef.current &&
        !activeElementRef.current.contains(document.activeElement)
      ) {
        setIsTextAreaFocused(false);
      }
    }, 0);
  };

  useEffect(() => {
    if (campaignType !== 'welcome') return;
    if (customizedTemplate?.settings?.filter?.send_right_away) {
      setCustomizedTemplate((prevState) => {
        const newState = _.set({...prevState}, 'settings.sending_date.hours_since_the_event', 0);
        return _.set(newState, 'settings.sending_date.fixed_time', 0);
      });
    } else {
      setCustomizedTemplate((prevState) =>
        _.set({...prevState}, 'settings.sending_date.hours_since_the_event', null)
      );
    }
  }, [customizedTemplate?.settings?.filter?.send_right_away]);

  useEffect(() => {
    if (customizedTemplate?.settings?.filter?.move_weekend === "0") {
      setCustomizedTemplate((prevState) =>
        _.set({...prevState}, 'settings.sending_date.exclude_weekdays', [])
      );
    }
    if (customizedTemplate?.settings?.filter?.move_weekend === "1") {
      setCustomizedTemplate((prevState) =>
        _.set({...prevState}, 'settings.sending_date.exclude_weekdays', [
          {
            day: 6, //move saturday to Monday after
            move_to: 1,
            next_week: true,
          },
          {
            day: 0, //move sunday to Monday after
            move_to: 1,
            next_week: true,
          }
        ])
      );
    }
    if (customizedTemplate?.settings?.filter?.move_weekend === "-1") {
      setCustomizedTemplate((prevState) =>
        _.set({...prevState}, 'settings.sending_date.exclude_weekdays', [
          {
            day: 6, //move saturday to Friday before
            move_to: 5,
            next_week: false,
          },
          {
            day: 0, //move sunday to Friday before
            move_to: 5,
            next_week: false,
          },
        ])
      );
    }
  }, [customizedTemplate?.settings?.filter?.move_weekend]);

  useEffect(() => {
    if (customizedTemplate?.settings?.sending_date?.same_day?.enabled) {
      setCustomizedTemplate((prevState) => {
        const newState = _.set({...prevState}, 'settings.sending_date.previous_day.send_time', null);
        return _.set(newState, 'settings.sending_date.previous_day.days_before', null);
      });
    } else {
      setCustomizedTemplate((prevState) => {
        const newState = _.set({...prevState}, 'settings.sending_date.same_day.send_time', null);
        return _.set(newState, 'settings.sending_date.same_day.days_before', null);
      });
    }
  }, [customizedTemplate?.settings?.sending_date?.same_day?.enabled]);

  const lastVisitRangePath = "settings.filter.last_visit_range";
  const excludePatientsWithFutureApptsPath =
    "settings.filter.exclude_patients_with_future_appointments";

  const hasLastVisitRange = (tpl) => {
    const range = _.get(tpl, lastVisitRangePath);
    return Array.isArray(range) && range.some((v) => v != null && v !== "");
  };

  useEffect(() => {
    if (
      hasLastVisitRange(customizedTemplate) &&
      _.get(customizedTemplate, excludePatientsWithFutureApptsPath)
    ) {
      setCustomizedTemplate((prev) =>
        _.set({ ...prev }, excludePatientsWithFutureApptsPath, false)
      );
    }
  }, [customizedTemplate?.settings?.filter?.last_visit_range]);

  useEffect(() => {
    const enabled = _.get(customizedTemplate, excludePatientsWithFutureApptsPath);
    if (enabled && hasLastVisitRange(customizedTemplate)) {
      setCustomizedTemplate((prev) =>
        _.set({ ...prev }, lastVisitRangePath, [])
      );
    }
  }, [customizedTemplate?.settings?.filter?.exclude_patients_with_future_appointments]);

  const handleNestedChange = (keyPath, value) => {
    setCustomizedTemplate((prevState) =>
      _.set({...prevState}, keyPath, value)
    );
  };

  useEffect(() => {
    fetchCampaigns();
    fetchCategories();
  }, []);

  useEffect(() => {
    setCustomizedTemplate({
      ...customizedTemplate,
      template: {
        ...(customizedTemplate?.template ?? {}),
        sms: {
          ...(customizedTemplate?.template?.sms ?? {}),
          ...((["reminder", 'review'].includes(campaignType))
            ? {
              template: campaignType === 'reminder' ? smsReminderTemplate : smsReviewTemplate,
            }
            : {
              template: "",
            }),
        },
      },
      settings: {
        ...customizedTemplate.settings,
        include_confirmation_link_in_sms: campaignType === "reminder",
        sending_date: {
          ...customizedTemplate.settings?.sending_date,
          hours_since_the_event: customizedTemplate.settings?.sending_date?.hours_since_the_event && customizedTemplate.settings?.sending_date?.hours_since_the_event.toString()
        }
      },
    });
    setCampaignPlaceholders(getCampaignPlaceholders(campaignType));
  }, [campaignType, campaignCategory]);

// Group campaigns by purpose and archived status
  const groupCampaignsByPurpose = campaigns.reduce(
    (acc, campaign) => {
      if (campaign.purpose) {
        const purpose = Camelize(campaign.purpose.replace(/[^-]+-/, ""));
        if (!acc[purpose]) {
          acc[purpose] = { active: [], archived: [] };
        }
        if (campaign.archived) {
          acc[purpose].archived.push(campaign);
        } else {
          acc[purpose].active.push(campaign);
        }
      }
      return acc;
    },
    {}
  );

  const handlePreviewClick = async (campaign) => {
    setIsLoading(true);
    try {
      const html = await getCampaignPreviewHtml(campaign);
      setPreviewContent({ __html: html });
    } catch (error) {
      console.error("Error loading preview content", error);
      setPreviewContent({ __html: "Failed to load content." });
    } finally {
      setIsLoading(false);
    }
    onOpen();
  };

  const createCampaign = async () => {
    if (campaignMedium === "Email" && campaignCategory) {
      await showEmailTemplates(campaignCategory);
    }
    if (campaignMedium === "SMS") {
      onSMSCampaignOpen();
    }
    if (campaignMedium === "Phone") {
      onPhoneCampaignOpen();
    }
    onAddCampaignClose();
  };

  const openAddCampaignModal = () => {
    setCustomizedTemplate(dataTemplate);
    setCampaignType("");
    setCampaignMedium("");
    setCampaignCategory("");
    setCampaignPlaceholders([]);
    onAddCampaignOpen();
  };

  const handleCampaignTypeChange = (event) => {
    setCampaignType(event.target.value);
    setCampaignPlaceholders(getCampaignPlaceholders(event.target.value));
    if (event.target.value !== "reminder") {
      setCampaignMedium("Email"); // Default to email if not 'Reminder'
    }
  };

  const handleCampaignMediumChange = (event) => {
    if (event.target.value !== "Email") {
      setCampaignCategory("");
    }
    setCampaignMedium(event.target.value);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomizedTemplate((prev) => ({
      ...prev,
      template_id: template.id,
      content: template.content
    }));
  };

  const confirmCloseBeePluginWithoutSaving = () => {
    dialogManager.open({
      onDeny: () => {
      },
      onAccept: () => {
        setIsBeePluginVisible(false);
        setIsTextAreaFocused(false);
        resetData();
      },
      acceptText: "Yes",
      denyText: "No",
      body: "Are you sure you want to close without saving?",
      header: "Confirm",
    });
  };

  const handleCustomizeTemplate = async () => {
    onTemplatesModalClose();
    await showTemplateEditor();
  };

  const handleSettingChange = (key, value, type) => {
    // Check for nested 'sending_date' changes
    if (type === "rangeInt") {
      // Handle range settings for specific filters

      const [filterKey, index] = key
        .split(".")
        .splice(-1)[0]
        .replace("]", "")
        .split("[");
      let newRange = customizedTemplate["settings"]["filter"][filterKey];
      if (Array.isArray(newRange)) {
        newRange[parseInt(index)] = value === "" ? undefined : parseInt(value);
        if (value === "") {
          newRange = [];
        } else if (newRange[0] > newRange[1]) {
          toast({
            title: "Invalid Range",
            description: "The 'to' value cannot be less than the 'from' value.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      } else {
        newRange = value === "" ? undefined : parseInt(value);
      }
      handleNestedChange(key.replace(/\[.+/, "").split("."), newRange);
      return;
    }
    if (type === "rangeDate") {
      // Handle range settings for specific filters

      const [filterKey, index] = key
        .split(".")
        .splice(-1)[0]
        .replace("]", "")
        .split("[");
      let newRange = customizedTemplate["settings"]["filter"][filterKey];
      newRange[parseInt(index)] = value === "" ? undefined : value;
      if (value === "") {
        newRange = [];
      } else if (newRange[0] > newRange[1]) {
        toast({
          title: "Invalid Range",
          description: "The 'to' value cannot be less than the 'from' value.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      handleNestedChange(key.replace(/\[.+/, "").split("."), newRange);
      if (filterKey === "last_visit_range" && _.get(customizedTemplate, excludePatientsWithFutureApptsPath) === true) {
        handleNestedChange(
          excludePatientsWithFutureApptsPath,
          false
        );
      }
      return;
    }
    handleNestedChange(key.split("."), value);
  };

  const changeArchiveStatus = async (campaignId, archived) => {
    try {
      const campaignData = campaigns.find(item => item._id === campaignId);
      const response = await cqAPI.put(`/campaigns?id=${campaignId}&archive_update=1`, {
        ...campaignData,
        archived,
      });

      if (response.type === "ok") {
        // Update state
        setCampaigns((prevCampaigns) =>
          prevCampaigns.map((campaign) =>
            campaign._id === campaignId ? { ...campaign, archived } : campaign
          )
        );
        toast({
          title: "Success",
          description: `Campaign ${archived ? 'archived' : 'unarchived'} successfully`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(response.msg);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    Object.keys(filterOptionEnabled).forEach((k) => {
      const campaignTypeConfig = CampaignTypes.find((c) => c.id === campaignType);
      const filterConfig = (campaignTypeConfig?.filters || []).find((f) => f.id === k);
      if (filterConfig?.type === "toggle_array") {
        setCustomizedTemplate({
          ...customizedTemplate,
          settings: {
            ...customizedTemplate["settings"],
            filter: {
              ...customizedTemplate["settings"]["filter"],
              [k.split(".").slice(-1)[0]]: filterOptionEnabled[k] ? 
                customizedTemplate.settings.filter[k.split(".").slice(-1)[0]] ?? [] : []
            },
          },
        });
      } else {
        setCustomizedTemplate({
          ...customizedTemplate,
          settings: {
            ...customizedTemplate["settings"],
            filter: {
              ...customizedTemplate["settings"]["filter"],
              [k.split(".").slice(-1)[0]]: {
                is_included:
                customizedTemplate.settings.filter[k.split(".").slice(-1)[0]]
                  ?.is_included,
                list: filterOptionEnabled[k]
                  ? customizedTemplate.settings.filter[k.split(".").slice(-1)[0]]
                  ?.list || []
                  : [],
              },
            },
          },
        });
      }
    });
  }, [filterOptionEnabled]);

  // A utility function to create a debounced function that returns a promise
  const debouncedLoad = (endpoint) => {
    const fetchOptions = debounce(
      (inputValue, callback) => {
        cqAPI
          .get(
            `/campaigns/filter?q=${inputValue}&type=${
              endpoint.split(".").slice(-1)[0]
            }`
          )
          .then((response) => {
            const options = response.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            callback(options);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
            callback([]);
          });
      },
      100,
      {
        leading: false,
        trailing: true,
      }
    );

    return (inputValue) =>
      new Promise((resolve) => {
        fetchOptions(inputValue, resolve);
      });
  };

  const loadOptions = (ep) => debouncedLoad(ep);

  const handleRefresh = async () => {
    setLoading(true);
    const response = await cqAPI.post("/campaigns/count-patients", {
      ...customizedTemplate,
      type: campaignMedium.toLowerCase(),
      purpose:
        campaignType === "reminder" ? "reminder" : `campaign-${campaignType}`,
    });
    setEstimatedPatients(response.data);
    setLoading(false); // Set loading to false after the API call
  };

  // Function to handle opening the modal
  const openSendTestModal = () => {
    setSendTestModalOpen(true);
  };

  // Function to handle closing the modal
  const closeSendTestModal = () => {
    setSendTestModalOpen(false);
    setTestDate(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        textAreaRef.current &&
        !textAreaRef.current.contains(event.target) &&
        placeholderModalRef.current &&
        !placeholderModalRef.current.contains(event.target)
      ) {
        setIsTextAreaFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [textAreaRef, placeholderModalRef]);

  const sendTestCampaign = async () => {
    const template = await getTemplate();
    if (!template) {
      return;
    }

    setUploadingTemplate(true);

    try {
      const response = await cqAPI.post("/campaigns/send", {
        campaign: {
          ...template,
          type: campaignMedium.toLowerCase(),
          purpose: campaignType === "reminder" ? "reminder" : `campaign-${campaignType}`,
          ...(selectedTemplate ? { template_id: selectedTemplate.id } : {}),
        },
        process_date: testDate.toISOString(),
      });

      setUploadingTemplate(false);

      if (response.type === "ok") {
        closeSendTestModal();
        toast({
          title: "Success",
          description: "Test campaign sent successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else if (response !== false) { // if it's not a confirmation reject
        console.log('Backend error:', response ? response.msg : 'Unknown error');
        toast({
          title: "Error",
          description: response ? response.msg : "An error occurred while sending the test campaign.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.log('Error sending test campaign:', error);
      console.log('Error response data:', error.response ? error.response.data : 'No response data');
      setUploadingTemplate(false);
      toast({
        title: "Error",
        description: error.response && error.response.data ? error.response.data.msg : "Failed to send the test campaign. Please ensure the date is at least 10 minutes in the future and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onSettingsBackClicked = async () => {
    setIsConfigVisible(false);
    switch (campaignMedium) {
      case "SMS":
        onSMSCampaignOpen();
        break;
      case "Phone":
        onPhoneCampaignOpen();
        break;
      case "Email":
        if (
          customizedTemplate?.html !== selectedTemplate.html
        ) {
          await handleCustomizeTemplate();
        } else {
          onTemplatesModalOpen();
        }
        break;
    }
  };

  const onSettingsCancelClicked = () => {
    dialogManager.open({
      onDeny: () => {
      },
      onAccept: () => {
        setIsConfigVisible(false);
        resetData();
      },
      acceptText: "Yes",
      denyText: "No",
      body: "Are you sure you want to cancel without saving?",
      header: "Confirm",
    });
  };

  const settingBgColor = useColorModeValue("gray.100", "gray.700");
  const bgColorfloat = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const linkColor = useColorModeValue("blue.500", "blue.200");

  const handleTemplateEditorBack = async () => {
    setIsBeePluginVisible(false);
    setIsTextAreaFocused(false);
    await showEmailTemplates(campaignCategory);
  }

  const handleTemplateEditorSave = () => {
    if (isBeePluginVisible && beeEditorInstance) {
      beeEditorInstance.save();
    }
  }

  const handleTemplateEditorClose = () => {
    setIsBeePluginVisible(false);
    setIsTextAreaFocused(false);
  };

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {(isTextAreaFocused ) && (<Portal>
        <Box
          className="placeholders-modal"
          bg={settingBgColor}
          ref={placeholderModalRef}
        >
          <PlaceholdersModal
            textAreaRef={textAreaRef}
            campaignMedium={campaignMedium}
            setCustomizedTemplate={setCustomizedTemplate}
            customizedTemplate={customizedTemplate}
            placeholders={campaignPlaceholders}
            templatePath={templatePath}
            onClose={confirmCloseBeePluginWithoutSaving}
            isCollapsible={true}
          />
        </Box>
            </Portal>
      )}
      {isConfigVisible && (
        <Box
          position="absolute"
          top="0"
          left="0"
          overflow="auto"
          right="0"
          bottom="0"
          bg={
            colorMode === "light" ? "rgba(255, 255, 255)" : "rgba(26, 32, 44)"
          }
          zIndex="overlay"
        >
          <Box
            p={4}
            overflowY="auto"
            width="100%"
            bg={colorMode === "light" ? "white" : "gray.800"}
            color={colorMode === "light" ? "black" : "white"}
          >
            {campaignType === "manual" &&
              (loading ? (
                <Flex
                  position="fixed"
                  top="10px"
                  right="10px"
                  p={4}
                  zIndex={100}
                  align="center"
                  bg={bgColorfloat}
                  borderRadius="md"
                  boxShadow="md"
                >
                  <Text>
                    Estimated number of patients who will receive this campaign:{" "}
                  </Text>
                  <Spinner/>
                </Flex>
              ) : (
                <Flex
                  position="fixed"
                  top="10px"
                  right="10px"
                  p={4}
                  zIndex={100}
                  align="center"
                  bg={bgColorfloat}
                  borderRadius="md"
                  boxShadow="md"
                >
                  <Text>
                    Estimated number of patients who will receive this campaign:{" "}
                    {estimatedPatients}
                  </Text>
                  <Link
                    marginLeft="20px"
                    cursor="pointer"
                    color={linkColor}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Link>
                  <Button
                    marginLeft="10px"
                    colorScheme="green"
                    onClick={openSendTestModal}
                  >
                    Send Test
                  </Button>
                </Flex>
              ))}
            <CampaignSettings
              template={customizedTemplate}
              medium={campaignMedium}
              campaignType={campaignType}
              getTemplateValue={getValue}
              handleSettingChange={handleSettingChange}
              settingBgColor={settingBgColor}
              filterOptionEnabled={filterOptionEnabled}
              setFilterOptionEnabled={setFilterOptionEnabled}
              toast={toast}
              colorMode={colorMode}
              isEditing={isEditing}
              loadOptions={loadOptions}
              onBackClicked={onSettingsBackClicked}
              onCancelClicked={onSettingsCancelClicked}
              onSaveClicked={saveCampaign}
              savingCampaign={savingCampaign}
              timezone={timezone}
            />
          </Box>
        </Box>
      )}
      <TemplateEditorModal
        isOpen={isBeePluginVisible}
        onBack={handleTemplateEditorBack}
        onSave={handleTemplateEditorSave}
        onClose={handleTemplateEditorClose}
      >
      </TemplateEditorModal>
      <Flex justifyContent="flex-end" mb={4}>
        <Button leftIcon={<AddIcon/>} onClick={openAddCampaignModal}>
          Add Campaign
        </Button>
      </Flex>
      <Tabs
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        variant="enclosed"
        bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
        borderRadius="16px"
        padding="16px 24px 24px 24px"
      >
        <TabList borderBottom="1px solid" borderColor="gray.200">
          {Object.keys(groupCampaignsByPurpose).map((purpose) => (
            <Tab
              key={purpose}
              padding="0px 16px 16px 16px"
              border="none"
              _selected={{
                borderBottom: `4px solid ${
                  colorMode === "light"
                    ? theme.colors.brand[300]
                    : theme.colors.lightBackground
                }`,
              }}
            >
              {Camelize(
                purpose.includes("-") ? purpose.split("-")[1] : purpose
              )}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {Object.entries(groupCampaignsByPurpose).map(([purpose, { active, archived }]) => (
            <TabPanel key={purpose}>
              <VStack spacing={4} align="stretch">
                <CampaignTableRow
                  title=""
                  campaigns={active.filter(
                    (c) => c.status !== "sent" && c.status !== "error"
                  )}
                  handlePreviewClick={handlePreviewClick}
                  changeArchiveStatus={changeArchiveStatus}
                  loadSettingsEditor={loadSettingsEditor}
                  loadTemplateEditor={loadTemplateEditor}
                />
                <CampaignTableRow
                  title="Successfully Sent"
                  campaigns={active.filter((c) => c.status === "sent")}
                  handlePreviewClick={handlePreviewClick}
                  changeArchiveStatus={changeArchiveStatus}
                  loadSettingsEditor={loadSettingsEditor}
                  loadTemplateEditor={loadTemplateEditor}
                />
                <CampaignTableRow
                  title="Errors"
                  campaigns={active.filter((c) => c.status === "error")}
                  handlePreviewClick={handlePreviewClick}
                  changeArchiveStatus={changeArchiveStatus}
                  loadSettingsEditor={loadSettingsEditor}
                  loadTemplateEditor={loadTemplateEditor}
                />
                <CampaignTableRow
                  title="Archived Campaigns"
                  campaigns={archived}
                  handlePreviewClick={handlePreviewClick}
                  changeArchiveStatus={changeArchiveStatus}
                  loadSettingsEditor={loadSettingsEditor}
                  loadTemplateEditor={loadTemplateEditor}
                />
              </VStack>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
      <AddCampaignModal
        isOpen={isAddCampaignOpen}
        onClose={onAddCampaignClose}
        campaignType={campaignType}
        campaignMedium={campaignMedium}
        campaignCategory={campaignCategory}
        handleCampaignTypeChange={handleCampaignTypeChange}
        handleCampaignMediumChange={handleCampaignMediumChange}
        setCampaignCategory={setCampaignCategory}
        categories={categories}
        createCampaign={createCampaign}
        resetData={resetData}
      />

      <SendTestModal
        isOpen={isSendTestModalOpen}
        onClose={closeSendTestModal}
        testDate={testDate}
        setTestDate={setTestDate}
        sendTestCampaign={sendTestCampaign}
        uploadingTemplate={uploadingTemplate}
        colorMode={colorMode}
      />
      <PreviewModal
        isOpen={isOpen}
        onClose={onClose}
        isLoading={isLoading}
        previewContent={previewContent}
      />
      <TemplatesModal
        isOpen={isTemplatesModalOpen}
        onClose={onTemplatesModalClose}
        categories={categories}
        campaignCategory={campaignCategory}
        templates={templates}
        selectedTemplate={selectedTemplate}
        handleTemplateSelect={handleTemplateSelect}
        customizeTemplate={handleCustomizeTemplate}
        setCustomizedTemplate={setCustomizedTemplate}
        setIsConfigVisible={setIsConfigVisible}
        onTemplatesModalClose={onTemplatesModalClose}
        saveCampaign={saveCampaign}
        saveEmailTemplate={saveEmailTemplate}
      />
      <SMSCampaignModal
        isOpen={isSMSCampaignOpen}
        campaignType={campaignType}
        onClose={onSMSCampaignClose}
        onAddCampaignOpen={onAddCampaignOpen}
        toast={toast}
        campaignCategory={campaignCategory}
        textAreaRef={textAreaRef}
        customizedTemplate={customizedTemplate}
        setCustomizedTemplate={setCustomizedTemplate}
        setIsConfigVisible={setIsConfigVisible}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        resetData={resetData}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        saveCampaign={saveCampaign}
      />
      <PhoneCampaignModal
        isOpen={isPhoneCampaignOpen}
        onClose={onPhoneCampaignClose}
        onAddCampaignOpen={onAddCampaignOpen}
        campaignType={campaignType}
        textAreaRef={textAreaRef}
        customizedTemplate={customizedTemplate}
        setCustomizedTemplate={setCustomizedTemplate}
        setIsConfigVisible={setIsConfigVisible}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
        resetData={resetData}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        saveCampaign={saveCampaign}
      />
    </Box>
  );
}

export default Campaigns;
